import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShieldCheck, 
  ShieldAlert,
  Save, 
  Zap, 
  CheckCircle, 
  CheckCircle2,
  XCircle,
  Sliders, 
  Layers, 
  Database,
  Wifi,
  Clock
} from 'lucide-react';
import { useAuth } from '@/lib/auth';

export function MicrosoftSentinelSettings() {
  const { session, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [simulating, setSimulating] = useState(false);
  const [simResult, setSimResult] = useState<any>(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);

  const userRole = (user?.role || '').toLowerCase().replace(/[-_]/g, ' ').trim();
  const isAllowedAdmin = userRole === 'super admin' || userRole === 'superadmin' || userRole === 'admin';

  if (!isAllowedAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] p-8 text-center space-y-4">
        <div className="w-16 h-16 rounded-2xl bg-rose-500/10 text-rose-500 border border-rose-500/20 flex items-center justify-center">
          <ShieldAlert className="w-8 h-8" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">Access Restricted to Administrators</h2>
        <p className="text-sm text-muted-foreground max-w-md">
          Azure Sentinel Integration settings, credentials, and automation controls are restricted exclusively to <strong>Administrators</strong>.
        </p>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 rounded-xl bg-secondary text-secondary-foreground hover:bg-muted border border-border text-xs font-semibold"
        >
          Return to Previous Page
        </button>
      </div>
    );
  }

  const [form, setForm] = useState({
    enabled: true,
    tenant_id: '005b5237-b81a-4886-bab3-9b45e5bb1489',
    workspace_id: 'd8d9343b-46bc-40fb-93d7-f5db164678e6',
    workspace_name: 'Pinochle-IND',
    subscription_id: 'be628e3f-97ba-4e0d-8307-9c7b172318d7',
    resource_group: 'sentinel-law',
    client_id: 'ff3e7b6b-1b79-45ee-aa8e-00ee912b75d5',
    client_secret: '',
    has_client_secret: false,
    api_url: 'https://management.azure.com',
    default_team_id: '22222222-2222-2222-2222-222222222222',
    auto_create_use_case: true,
    auto_notification: true,
    auto_email: true,
    duplicate_handling: 'UPDATE_EXISTING',
    portal_url: 'http://localhost:5173',
    last_synced_at: null as string | null,
    severity_mapping: {
      Informational: 'Low',
      Low: 'Low',
      Medium: 'Medium',
      High: 'High',
      Critical: 'Critical'
    }
  });

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/admin/sentinel/config', {
        headers: { 'Authorization': `Bearer ${session?.access_token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setForm(prev => ({
          ...prev,
          ...data,
          client_secret: '', // Keep client secret input clean/empty on load
          has_client_secret: Boolean(data.has_client_secret),
          severity_mapping: {
            ...prev.severity_mapping,
            ...(data.severity_mapping || {})
          }
        }));
      }
    } catch (e) {
      console.error('Failed to load Sentinel config:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (session?.access_token) {
      fetchConfig();
    }
  }, [session?.access_token]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setSaveSuccess(null);
      setSaveError(null);

      const payload: any = { ...form };
      // If client_secret was left blank, do not overwrite existing backend secret
      if (!payload.client_secret || payload.client_secret.trim() === '') {
        delete payload.client_secret;
      }

      const res = await fetch('/api/admin/sentinel/config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const updated = await res.json();
        setForm(prev => ({
          ...prev,
          ...updated,
          client_secret: '',
          has_client_secret: Boolean(updated.has_client_secret)
        }));
        setSaveSuccess('Microsoft Sentinel integration settings successfully saved and active.');
        setTimeout(() => setSaveSuccess(null), 5000);
      } else {
        const errJson = await res.json().catch(() => ({}));
        setSaveError(errJson.error || 'Failed to save Sentinel configuration.');
      }
    } catch (err: any) {
      setSaveError(err.message || 'Network error saving configuration.');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      setTestResult(null);
      const res = await fetch('/api/sentinel/test-connection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          tenant_id: form.tenant_id,
          client_id: form.client_id,
          client_secret: form.client_secret || undefined,
          subscription_id: form.subscription_id,
          resource_group: form.resource_group,
          workspace_name: form.workspace_name,
          workspace_id: form.workspace_id,
          api_url: form.api_url
        })
      });

      const data = await res.json();
      setTestResult(data);
    } catch (err: any) {
      setTestResult({
        success: false,
        error: err.message,
        stages: {
          authentication: { success: false, message: err.message },
          workspace_connectivity: { success: false, message: 'Not reached' },
          incident_retrieval: { success: false, message: 'Not reached' }
        }
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSendTest = async () => {
    try {
      setSimulating(true);
      setSimResult(null);
      const res = await fetch('/api/integrations/sentinel/test-incident', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({})
      });

      if (res.ok) {
        const data = await res.json();
        setSimResult(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSimulating(false);
    }
  };

  const updateSev = (key: string, val: string) => {
    setForm(prev => ({
      ...prev,
      severity_mapping: {
        ...prev.severity_mapping,
        [key]: val
      }
    }));
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-xs text-muted-foreground">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-4" />
        Loading Microsoft Sentinel configuration...
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-border pb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-2xl bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">Microsoft Sentinel Integration</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Admin • Settings • Integrations • Azure OAuth 2.0 Ingestion & Step 1 SOC Use Case Auto-Provisioning
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleTestConnection}
            disabled={testingConnection}
            className="px-4 py-2 rounded-xl bg-indigo-600/10 hover:bg-indigo-600/20 text-indigo-400 border border-indigo-500/30 text-xs font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            <Wifi className="w-3.5 h-3.5" />
            {testingConnection ? 'Testing Connection...' : 'Test Connection'}
          </button>

          <button
            type="button"
            onClick={handleSendTest}
            disabled={simulating}
            className="px-4 py-2 rounded-xl bg-secondary hover:bg-muted text-secondary-foreground border border-border text-xs font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            <Zap className="w-3.5 h-3.5 text-indigo-400" />
            {simulating ? 'Simulating...' : 'Send Test Incident'}
          </button>
        </div>
      </div>

      {/* Save Alerts */}
      {saveSuccess && (
        <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-400 flex items-center justify-between animate-in fade-in duration-200">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            <span>{saveSuccess}</span>
          </div>
          <button onClick={() => setSaveSuccess(null)}>✕</button>
        </div>
      )}

      {saveError && (
        <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-xs text-rose-400 flex items-center justify-between animate-in fade-in duration-200">
          <div className="flex items-center gap-2">
            <XCircle className="w-4 h-4" />
            <span>{saveError}</span>
          </div>
          <button onClick={() => setSaveError(null)}>✕</button>
        </div>
      )}

      {/* Test Connection Diagnostic Results */}
      {testResult && (
        <div className={`p-5 rounded-2xl border text-xs space-y-4 animate-in fade-in duration-200 ${
          testResult.success 
            ? 'bg-emerald-500/10 border-emerald-500/20' 
            : 'bg-rose-500/10 border-rose-500/20'
        }`}>
          <div className="flex items-center justify-between border-b border-border/50 pb-3">
            <div className="flex items-center gap-2.5">
              {testResult.success ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              ) : (
                <XCircle className="w-5 h-5 text-rose-400" />
              )}
              <span className="font-bold text-sm text-foreground">
                {testResult.success ? 'Sentinel Connection Test: SUCCESS' : 'Sentinel Connection Test: FAILED'}
              </span>
            </div>
            <button onClick={() => setTestResult(null)} className="text-muted-foreground hover:text-foreground">✕</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Stage 1: Authentication */}
            <div className="p-3 rounded-xl bg-card border border-border space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-foreground">1. Azure Entra ID</span>
                {testResult.stages?.authentication?.success ? (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400">PASSED</span>
                ) : (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-rose-500/20 text-rose-400">FAILED</span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">{testResult.stages?.authentication?.message}</p>
              {testResult.stages?.authentication?.latency_ms != null && (
                <span className="text-[10px] text-muted-foreground block font-mono">
                  Latency: {testResult.stages.authentication.latency_ms}ms
                </span>
              )}
            </div>

            {/* Stage 2: Workspace Connectivity */}
            <div className="p-3 rounded-xl bg-card border border-border space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-foreground">2. Workspace Connectivity</span>
                {testResult.stages?.workspace_connectivity?.success ? (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400">PASSED</span>
                ) : (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-rose-500/20 text-rose-400">FAILED</span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">{testResult.stages?.workspace_connectivity?.message}</p>
            </div>

            {/* Stage 3: Incidents Retrieval */}
            <div className="p-3 rounded-xl bg-card border border-border space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-foreground">3. Security API Access</span>
                {testResult.stages?.incident_retrieval?.success ? (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400">PASSED</span>
                ) : (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-rose-500/20 text-rose-400">FAILED</span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">{testResult.stages?.incident_retrieval?.message}</p>
            </div>
          </div>

          {testResult.error && (
            <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-[11px] text-rose-300 font-mono">
              Diagnostic Error: {testResult.error}
            </div>
          )}
        </div>
      )}

      {/* Test Incident Result Banner */}
      {simResult && (
        <div className="p-4 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 text-xs space-y-2 animate-in fade-in duration-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-indigo-400 font-bold">
              <CheckCircle className="w-4 h-4" />
              <span>Test Sentinel Incident Successfully Ingested!</span>
            </div>
            <button onClick={() => setSimResult(null)} className="text-muted-foreground hover:text-foreground">✕</button>
          </div>
          <p className="text-muted-foreground">
            Incident ID: <strong className="text-foreground font-mono">{simResult.external_incident_id}</strong> • Use Case Number: <strong className="text-foreground font-mono">{simResult.use_case_number}</strong> • Team: <strong className="text-blue-400 font-semibold">{simResult.team}</strong> • Severity: <strong className="text-amber-400">{simResult.severity}</strong>
          </p>
        </div>
      )}

      {/* Main Settings Form */}
      <form onSubmit={handleSave} className="space-y-6">
        
        {/* Section 1: Integration Status */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold text-foreground">Integration Activation</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Enable or disable automated Microsoft Sentinel incident ingestion & Use Case creation.
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={form.enabled} 
                onChange={e => setForm({ ...form, enabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600" />
            </label>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-border/60 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              Last Synchronized At:
            </span>
            <span className="font-mono text-foreground">
              {form.last_synced_at ? new Date(form.last_synced_at).toLocaleString() : 'Never synced'}
            </span>
          </div>
        </div>

        {/* Section 2: Azure App Registration & Sentinel Workspace */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
          <h2 className="text-sm font-bold text-foreground flex items-center gap-2">
            <Database className="w-4 h-4 text-indigo-400" /> Azure App Registration & Workspace Credentials
          </h2>
          <p className="text-xs text-muted-foreground">
            Server-to-server OAuth 2.0 parameters. Client secret is securely encrypted and never exposed to the frontend.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Tenant ID</label>
              <input
                type="text"
                value={form.tenant_id}
                onChange={e => setForm({ ...form, tenant_id: e.target.value })}
                className="w-full text-xs font-mono bg-background border border-border rounded-xl px-3 py-2 text-foreground focus:outline-none focus:border-indigo-500"
                placeholder="e.g. 005b5237-b81a-4886-bab3-..."
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Client ID (App Registration)</label>
              <input
                type="text"
                value={form.client_id}
                onChange={e => setForm({ ...form, client_id: e.target.value })}
                className="w-full text-xs font-mono bg-background border border-border rounded-xl px-3 py-2 text-foreground focus:outline-none focus:border-indigo-500"
                placeholder="e.g. ff3e7b6b-1b79-45ee-..."
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1 flex items-center justify-between">
                <span>Client Secret</span>
                {form.has_client_secret && (
                  <span className="text-[10px] font-normal text-emerald-400 font-sans">
                    ✓ Configured in backend
                  </span>
                )}
              </label>
              <input
                type="password"
                value={form.client_secret}
                onChange={e => setForm({ ...form, client_secret: e.target.value })}
                placeholder={form.has_client_secret ? '•••••••••••• (Leave blank to keep existing secret)' : 'Enter Client Secret'}
                className="w-full text-xs font-mono bg-background border border-border rounded-xl px-3 py-2 text-foreground focus:outline-none focus:border-indigo-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Sentinel Resource / API URL</label>
              <input
                type="text"
                value={form.api_url}
                onChange={e => setForm({ ...form, api_url: e.target.value })}
                className="w-full text-xs font-mono bg-background border border-border rounded-xl px-3 py-2 text-foreground focus:outline-none focus:border-indigo-500"
                placeholder="https://management.azure.com"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Subscription ID</label>
              <input
                type="text"
                value={form.subscription_id}
                onChange={e => setForm({ ...form, subscription_id: e.target.value })}
                className="w-full text-xs font-mono bg-background border border-border rounded-xl px-3 py-2 text-foreground focus:outline-none focus:border-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Resource Group</label>
              <input
                type="text"
                value={form.resource_group}
                onChange={e => setForm({ ...form, resource_group: e.target.value })}
                className="w-full text-xs bg-background border border-border rounded-xl px-3 py-2 text-foreground focus:outline-none focus:border-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Workspace ID (Log Analytics GUID)</label>
              <input
                type="text"
                value={form.workspace_id}
                onChange={e => setForm({ ...form, workspace_id: e.target.value })}
                className="w-full text-xs font-mono bg-background border border-border rounded-xl px-3 py-2 text-foreground focus:outline-none focus:border-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Workspace Name</label>
              <input
                type="text"
                value={form.workspace_name}
                onChange={e => setForm({ ...form, workspace_name: e.target.value })}
                className="w-full text-xs bg-background border border-border rounded-xl px-3 py-2 text-foreground focus:outline-none focus:border-indigo-500"
                required
              />
            </div>

          </div>
        </div>

        {/* Section 3: Automated Workflow & Team Assignment */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
          <h2 className="text-sm font-bold text-foreground flex items-center gap-2">
            <Layers className="w-4 h-4 text-indigo-400" /> Automated Workflow & Team Assignment
          </h2>

          <div className="space-y-4 pt-2">
            
            <div className="flex items-center justify-between p-3.5 rounded-xl border border-border/60 bg-muted/20">
              <div>
                <span className="text-xs font-bold text-foreground">Default Assigned Team</span>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Incoming Sentinel incidents and created use cases are restricted exclusively to this team.
                </p>
              </div>
              <div className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/20">
                SOC Team (Security / SOC)
              </div>
            </div>

            <div className="flex items-center justify-between p-3.5 rounded-xl border border-border/60 bg-muted/20">
              <div>
                <span className="text-xs font-bold text-foreground">Auto Create SOC Use Case</span>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Automatically provisions a Step 1 (CREATED) Use Case in the existing 15-stage workflow.
                </p>
              </div>
              <input
                type="checkbox"
                checked={form.auto_create_use_case}
                onChange={e => setForm({ ...form, auto_create_use_case: e.target.checked })}
                className="w-4 h-4 accent-indigo-600 cursor-pointer"
              />
            </div>

            <div className="flex items-center justify-between p-3.5 rounded-xl border border-border/60 bg-muted/20">
              <div>
                <span className="text-xs font-bold text-foreground">Auto In-App Notification</span>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Dispatch real-time in-app notification to all SOC Team members when an incident is ingested.
                </p>
              </div>
              <input
                type="checkbox"
                checked={form.auto_notification}
                onChange={e => setForm({ ...form, auto_notification: e.target.checked })}
                className="w-4 h-4 accent-indigo-600 cursor-pointer"
              />
            </div>

            <div className="flex items-center justify-between p-3.5 rounded-xl border border-border/60 bg-muted/20">
              <div>
                <span className="text-xs font-bold text-foreground">Auto Email Notification</span>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Record email notification event to the SOC Team distribution list.
                </p>
              </div>
              <input
                type="checkbox"
                checked={form.auto_email}
                onChange={e => setForm({ ...form, auto_email: e.target.checked })}
                className="w-4 h-4 accent-indigo-600 cursor-pointer"
              />
            </div>

            <div className="flex items-center justify-between p-3.5 rounded-xl border border-border/60 bg-muted/20">
              <div>
                <span className="text-xs font-bold text-foreground">Duplicate Incident Handling</span>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Unique key: Organization ID + Sentinel Workspace ID + Sentinel Incident ID. Existing incidents update telemetry and record audit logs without duplicate ticket creation.
                </p>
              </div>
              <span className="text-xs font-mono font-semibold px-2.5 py-1 rounded bg-secondary text-secondary-foreground">
                UPDATE_EXISTING
              </span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Compass Portal URL (for notifications & email links)</label>
              <input
                type="text"
                value={form.portal_url}
                onChange={e => setForm({ ...form, portal_url: e.target.value })}
                className="w-full text-xs font-mono bg-background border border-border rounded-xl px-3 py-2 text-foreground focus:outline-none focus:border-indigo-500"
              />
            </div>

          </div>
        </div>

        {/* Section 4: Configurable Severity Mapping */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
          <h2 className="text-sm font-bold text-foreground flex items-center gap-2">
            <Sliders className="w-4 h-4 text-indigo-400" /> Configurable Severity Mapping
          </h2>
          <p className="text-xs text-muted-foreground">
            Map Microsoft Sentinel incoming incident severities to Compass SOC Use Case priorities.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 pt-2">
            {[
              { key: 'Informational', label: 'Informational' },
              { key: 'Low', label: 'Low' },
              { key: 'Medium', label: 'Medium' },
              { key: 'High', label: 'High' },
              { key: 'Critical', label: 'Critical' }
            ].map(item => (
              <div key={item.key} className="p-3.5 rounded-xl border border-border/60 bg-muted/20 space-y-1.5">
                <span className="text-[11px] font-bold text-foreground block">{item.label}</span>
                <select
                  value={(form.severity_mapping as any)[item.key] || 'Medium'}
                  onChange={e => updateSev(item.key, e.target.value)}
                  className="w-full text-xs bg-background border border-border rounded-lg px-2.5 py-1.5 text-foreground focus:outline-none focus:border-indigo-500"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold flex items-center gap-2 shadow-lg shadow-indigo-600/20 transition-all disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving Configuration...' : 'Save Configuration'}
          </button>
        </div>

      </form>

    </div>
  );
}
